import React, { useState } from 'react';
import { X, Copy, Link as LinkIcon, Check, Trash2 } from 'lucide-react';
import { pvSharingService } from '../services/api';

const SharePVModal = ({ isOpen, onClose, pv, onShareGenerated, onShareRevoked }) => {
  const [isGenerating, setIsGenerating] = useState(false);
  const [shareUrl, setShareUrl] = useState(null);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState(null);

  const handleGenerateLink = async () => {
    setIsGenerating(true);
    setError(null);
    
    try {
      const response = await pvSharingService.generateShareLink(pv.id);
      const fullUrl = `${window.location.origin}/shared/${response.share_token}`;
      setShareUrl(fullUrl);
      if (onShareGenerated) {
        onShareGenerated(pv.id, response.share_token);
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Erreur lors de la génération du lien');
    } finally {
      setIsGenerating(false);
    }
  };

  const handleCopyLink = () => {
    if (shareUrl) {
      navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleRevokeLink = async () => {
    if (!window.confirm('Êtes-vous sûr de vouloir révoquer ce lien de partage ?')) {
      return;
    }

    setIsGenerating(true);
    setError(null);
    
    try {
      await pvSharingService.revokeShareLink(pv.id);
      setShareUrl(null);
      if (onShareRevoked) {
        onShareRevoked(pv.id);
      }
      onClose();
    } catch (err) {
      setError(err.response?.data?.detail || 'Erreur lors de la révocation du lien');
    } finally {
      setIsGenerating(false);
    }
  };

  React.useEffect(() => {
    if (isOpen && pv?.share_enabled && pv?.share_token) {
      const fullUrl = `${window.location.origin}/shared/${pv.share_token}`;
      setShareUrl(fullUrl);
    } else {
      setShareUrl(null);
    }
    setCopied(false);
    setError(null);
  }, [isOpen, pv]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b px-6 py-4 flex justify-between items-center">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">Partager le PV</h2>
            <p className="text-sm text-gray-600 mt-1">
              PV du {new Date(pv.date).toLocaleDateString('fr-FR')}
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X size={24} />
          </button>
        </div>

        {/* Content */}
        <div className="px-6 py-6 space-y-6">
          {/* Instructions */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <h3 className="font-medium text-blue-900 mb-2">
              📋 Comment ça marche ?
            </h3>
            <ul className="text-sm text-blue-800 space-y-1">
              <li>• Générez un lien de partage en lecture seule</li>
              <li>• Les personnes avec le lien devront saisir le mot de passe : <strong>TCCC1468</strong></li>
              <li>• Elles pourront voir le PV complet sans pouvoir le modifier</li>
              <li>• Vous pouvez révoquer l'accès à tout moment</li>
            </ul>
          </div>

          {/* Error Message */}
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <p className="text-sm text-red-800">{error}</p>
            </div>
          )}

          {/* Share Link Section */}
          {!shareUrl ? (
            <div className="text-center py-8">
              <LinkIcon size={48} className="mx-auto text-gray-400 mb-4" />
              <p className="text-gray-600 mb-4">
                Aucun lien de partage actif pour ce PV
              </p>
              <button
                onClick={handleGenerateLink}
                disabled={isGenerating}
                className="inline-flex items-center px-6 py-3 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isGenerating ? (
                  <>
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                    Génération en cours...
                  </>
                ) : (
                  <>
                    <LinkIcon size={20} className="mr-2" />
                    Générer un lien de partage
                  </>
                )}
              </button>
            </div>
          ) : (
            <div className="space-y-4">
              {/* Share URL Display */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Lien de partage
                </label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={shareUrl}
                    readOnly
                    className="flex-1 px-4 py-3 border border-gray-300 rounded-lg bg-gray-50 text-sm font-mono"
                  />
                  <button
                    onClick={handleCopyLink}
                    className="px-4 py-3 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors flex items-center gap-2"
                  >
                    {copied ? (
                      <>
                        <Check size={20} />
                        Copié !
                      </>
                    ) : (
                      <>
                        <Copy size={20} />
                        Copier
                      </>
                    )}
                  </button>
                </div>
              </div>

              {/* Password Info */}
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                <p className="text-sm text-amber-800">
                  <strong>🔒 Mot de passe requis :</strong> TCCC1468
                </p>
                <p className="text-xs text-amber-700 mt-1">
                  Les personnes avec ce lien devront saisir ce mot de passe pour accéder au PV.
                </p>
              </div>

              {/* Revoke Button */}
              <div className="pt-4 border-t">
                <button
                  onClick={handleRevokeLink}
                  disabled={isGenerating}
                  className="inline-flex items-center px-4 py-2 text-red-600 border border-red-300 rounded-lg hover:bg-red-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Trash2 size={18} className="mr-2" />
                  Révoquer le lien de partage
                </button>
                <p className="text-xs text-gray-500 mt-2">
                  Le lien ne sera plus accessible après révocation
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 bg-gray-50 border-t px-6 py-4 flex justify-end">
          <button
            onClick={onClose}
            className="px-6 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
          >
            Fermer
          </button>
        </div>
      </div>
    </div>
  );
};

export default SharePVModal;
